/**
 * KillSwitchToggles — 4 boolean switches for a campaign (Story 7.5 AC2).
 *
 * Each switch is a plain accessible checkbox rendered as a pill. The wire
 * contract (AC4) is pass-through: the parent gets the current value on every
 * flip and decides whether to re-enable a switch that was previously turned
 * off — the server never re-enables automatically.
 *
 * We keep this as a native input rather than pulling shadcn's `Switch` so
 * the admin panel stays on the current minimal shadcn set (no extra Radix
 * dependency for one form).
 */

import { Label } from '@/components/ui/label';

export interface KillSwitchValue {
  discountEnabled: boolean;
  audioEnabled: boolean;
  followupEnabled: boolean;
  callbackEnabled: boolean;
}

export const KILL_SWITCH_DEFAULT: KillSwitchValue = {
  discountEnabled: true,
  audioEnabled: true,
  followupEnabled: true,
  callbackEnabled: true,
};

interface SwitchConfig {
  key: keyof KillSwitchValue;
  label: string;
  helper: string;
}

const SWITCHES: SwitchConfig[] = [
  {
    key: 'discountEnabled',
    label: 'Desconto',
    helper: 'Permite que o agente ofereça link com desconto.',
  },
  {
    key: 'audioEnabled',
    label: 'Áudio',
    helper: 'Permite que o agente transcreva e responda a áudios.',
  },
  {
    key: 'followupEnabled',
    label: 'Follow-up',
    helper: 'Envia mensagens de follow-up automáticas.',
  },
  {
    key: 'callbackEnabled',
    label: 'Callback',
    helper: 'Agenda re-engajamento quando o cliente pede para falar depois.',
  },
];

interface Props {
  value: KillSwitchValue;
  onChange: (next: KillSwitchValue) => void;
  disabled?: boolean;
}

export function KillSwitchToggles({ value, onChange, disabled }: Props) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {SWITCHES.map((s) => {
        const id = `kill-switch-${s.key}`;
        return (
          <div
            key={s.key}
            className="flex items-start justify-between rounded-md border bg-card p-3"
          >
            <div className="pr-4">
              <Label htmlFor={id} className="cursor-pointer">
                {s.label}
              </Label>
              <p className="text-xs text-muted-foreground">{s.helper}</p>
            </div>
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                id={id}
                type="checkbox"
                role="switch"
                aria-label={s.label}
                className="peer sr-only"
                checked={value[s.key]}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, [s.key]: e.target.checked })}
              />
              <span
                aria-hidden
                className="h-5 w-9 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-disabled:opacity-50"
              />
              <span
                aria-hidden
                className="absolute left-0.5 top-0.5 size-4 rounded-full bg-background shadow transition-transform peer-checked:translate-x-4"
              />
            </label>
          </div>
        );
      })}
    </div>
  );
}

export default KillSwitchToggles;
